import { Component, Input, Output, EventEmitter, ViewChild, signal, computed, ChangeDetectionStrategy, CUSTOM_ELEMENTS_SCHEMA, AfterViewInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { GameEngineComponent } from '../../game-engine/game-engine.component';
import { ArOptions } from '../../services/ar.service';

/**
 * Experience Preview Component
 *
 * A reusable "dumb" presentational component that renders game experiences
 * identically to how the Experience app would render them.
 *
 * Features:
 * - 3D grid AR/VR background with glow effects
 * - Integrated GameEngineComponent for game rendering
 * - Manual camera controls (zoom, pan, rotate, tilt)
 * - OrbitControls for mouse/touch navigation
 * - Zoom percentage overlay
 * - Fully standalone (no data fetching logic)
 *
 * Usage:
 * ```html
 * <lib-experience-preview
 *   [gameTypeId]="'wheel'"
 *   [config]="wheelSettings"
 *   (statusChange)="handleStatus($event)">
 * </lib-experience-preview>
 * ```
 *
 * Context 1: Game Settings Dialog
 * - Receives live settings from configurator form
 * - Updates in real-time as user adjusts settings
 *
 * Context 2: Standalone Preview Route
 * - Receives settings from selected campaign short code
 * - Allows testing different short codes
 */
@Component({
  selector: 'lib-experience-preview',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    GameEngineComponent,
  ],
  templateUrl: './experience-preview.component.html',
  styleUrls: ['./experience-preview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ExperiencePreviewComponent implements AfterViewInit, OnDestroy {
  // Inputs
  @Input({ required: true }) gameTypeId!: string;
  @Input({ required: true }) config!: unknown;
  @Input() shortCode?: string; // Optional short code for scoping camera state

  private _enableAR = false;
  @Input()
  set enableAR(value: boolean) {
    this._enableAR = value;
  }
  get enableAR(): boolean {
    return this._enableAR;
  }

  @Input() arMarkerPattern?: string; // AR marker pattern URL
  @Input() arOptions?: Partial<ArOptions>; // AR configuration options (patternRatio, smoothCount, etc.)

  // Outputs
  @Output() statusChange = new EventEmitter<GameStatus>();

  // ResizeObserver for handling viewport changes (delegated to GameEngine)
  private resizeObserver: ResizeObserver | null = null;

  // Computed: Extract getPrizeIndex from config if present
  protected configData = computed(() => {
    const config = this.config as any;
    return config || {};
  });

  protected getPrizeIndex = computed(() => {
    const config = this.configData();
    return config.getPrizeIndex || undefined;
  });

  // CTA Button configuration for preview
  protected ctaButtonConfig = computed(() => {
    // Show CTA button for wheel games in preview mode
    if (this.gameTypeId === 'wheel') {
      return {
        enabled: true,
        text: 'Spin',
        backgroundColor: '#6200ea',
        textColor: '#ffffff',
        position: { bottom: '80px' }
      };
    }
    // Show CTA button for game-board games (multiple rolls)
    if (this.gameTypeId === 'game-board' || this.gameTypeId === 'gameboard') {
      return {
        enabled: true,
        text: 'Roll',
        backgroundColor: '#6200ea',
        textColor: '#ffffff',
        position: { bottom: '80px' }
      };
    }
    // Scratchcard doesn't need CTA (tap-to-scratch)
    return { enabled: false, text: '' };
  });

  // ViewChild for game engine
  @ViewChild(GameEngineComponent) gameEngine?: GameEngineComponent;

  // Constants for camera calculations
  private static readonly DEFAULT_ZOOM_PERCENTAGE = 30;
  private static readonly ZOOM_DISTANCE_FACTOR = 10;
  private static readonly MIN_ZOOM_DISPLAY = 20;
  private static readonly MAX_ZOOM_DISPLAY = 200;
  private static readonly DEFAULT_CAMERA_DIRECTION = { x: 0, y: 0, z: 5 };
  private static readonly CONTROLS_INIT_DELAY = 500;
  private static readonly CONTROLS_RETRY_DELAY = 1000;

  // UI State
  protected previewZoom = signal(100); // Zoom percentage for UI display
  protected isControlsLocked = signal(false); // Camera lock state (false = OrbitControls enabled)

  // Camera state for initial positioning
  protected initialCameraPosition = computed(() => {
    const state = this.getStoredCameraState();
    if (state?.position) {
      return state.position;
    }
    // Default: 30% zoom (distance = 10 / 0.3 = 33.33)
    const defaultDistance = ExperiencePreviewComponent.ZOOM_DISTANCE_FACTOR / (ExperiencePreviewComponent.DEFAULT_ZOOM_PERCENTAGE / 100);
    const direction = ExperiencePreviewComponent.DEFAULT_CAMERA_DIRECTION;
    const magnitude = Math.sqrt(direction.x ** 2 + direction.y ** 2 + direction.z ** 2);
    return {
      x: (direction.x / magnitude) * defaultDistance,
      y: (direction.y / magnitude) * defaultDistance,
      z: (direction.z / magnitude) * defaultDistance
    };
  });

  // Store camera target separately for setting after OrbitControls init
  private storedCameraTarget?: { x: number; y: number; z: number };

  // OrbitControls change listener (for cleanup)
  private orbitControlsChangeListener?: () => void;

  // Static in-memory storage for camera state (persists across preview refreshes, clears on navigation)
  private static cameraStateMap = new Map<string, { position: { x: number; y: number; z: number }; target: { x: number; y: number; z: number } }>();

  /**
   * Get storage key for current game context
   * Uses shortCode if available (unique per campaign), otherwise falls back to gameTypeId
   */
  private getCameraStateKey(): string {
    return this.shortCode || this.gameTypeId;
  }

  constructor(private cdr: ChangeDetectorRef) {
    // Load camera target for later use
    const state = this.getStoredCameraState();
    if (state) {
      this.storedCameraTarget = state.target;
    }
  }

  /**
   * Get initial camera state from in-memory storage, or return null for default positioning
   * Camera state persists during preview refreshes but clears when navigating away
   */
  private getStoredCameraState(): { position: { x: number; y: number; z: number }; target: { x: number; y: number; z: number } } | null {
    const key = this.getCameraStateKey();
    return ExperiencePreviewComponent.cameraStateMap.get(key) || null;
  }

  /**
   * Setup OrbitControls with event listener and restore camera target
   * Extracted to avoid code duplication
   */
  private setupOrbitControls(controls: any): void {
    // Camera position already set via initialCameraPosition input
    // Just need to set the target if we have a stored state (otherwise default [0,0,0] is fine)
    if (this.storedCameraTarget) {
      controls.target.set(this.storedCameraTarget.x, this.storedCameraTarget.y, this.storedCameraTarget.z);
    }
    controls.update();

    // Create listener that updates zoom percentage whenever OrbitControls changes
    this.orbitControlsChangeListener = () => {
      this.updateZoomPercentage();
    };
    controls.addEventListener('change', this.orbitControlsChangeListener);
    this.updateZoomPercentage();
  }

  ngAfterViewInit(): void {
    // Wait a bit for OrbitControls to initialize, then set target and update zoom
    setTimeout(() => {
      const controls = this.gameEngine?.getOrbitControls();

      if (controls) {
        this.setupOrbitControls(controls);
      } else {
        // Retry once after delay if controls not immediately available
        setTimeout(() => {
          const retryControls = this.gameEngine?.getOrbitControls();
          if (retryControls) {
            this.setupOrbitControls(retryControls);
          }
        }, ExperiencePreviewComponent.CONTROLS_RETRY_DELAY);
      }
    }, ExperiencePreviewComponent.CONTROLS_INIT_DELAY);

    // GameEngine component has its own ResizeObserver that watches its host element,
    // so resizing is automatically handled - no additional setup needed here
    console.log('[ExperiencePreview] View initialized, resize handling delegated to GameEngine');
  }

  ngOnDestroy(): void {
    // Clean up event listener
    const controls = this.gameEngine?.getOrbitControls();
    if (controls && this.orbitControlsChangeListener) {
      controls.removeEventListener('change', this.orbitControlsChangeListener);
    }

    // Clean up resize observer if exists
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  /**
   * Toggle camera lock state
   * When locked: OrbitControls disabled, allows game interaction
   * When unlocked: OrbitControls enabled, camera can be moved
   */
  toggleCameraLock(): void {
    const controls = this.gameEngine?.getOrbitControls();
    if (controls) {
      const newLockState = !this.isControlsLocked();
      controls.enabled = !newLockState;
      this.isControlsLocked.set(newLockState);
    }
  }

  /**
   * Update zoom percentage based on actual camera distance
   * Uses inverse relationship: closer = higher percentage
   * Clamped to 20%-200% range for preview display
   */
  private updateZoomPercentage(): void {
    const controls = this.gameEngine?.getOrbitControls();
    if (controls) {
      const distance = controls.object.position.distanceTo(controls.target);

      // Store complete camera state in memory for restoration on preview refresh
      // Scoped by shortCode (or gameTypeId) so each campaign has independent camera position
      const cameraState = {
        position: {
          x: controls.object.position.x,
          y: controls.object.position.y,
          z: controls.object.position.z
        },
        target: {
          x: controls.target.x,
          y: controls.target.y,
          z: controls.target.z
        }
      };
      const key = this.getCameraStateKey();
      ExperiencePreviewComponent.cameraStateMap.set(key, cameraState);

      // Simple inverse: ZOOM_DISTANCE_FACTOR / distance gives natural percentage feel
      // distance=10 -> 100%, distance=5 -> 200%, distance=50 -> 20%
      const zoom = Math.round((ExperiencePreviewComponent.ZOOM_DISTANCE_FACTOR / distance) * 100);
      // Clamp to configured min/max range for preview display
      const clampedZoom = Math.max(
        ExperiencePreviewComponent.MIN_ZOOM_DISPLAY,
        Math.min(ExperiencePreviewComponent.MAX_ZOOM_DISPLAY, zoom)
      );
      this.previewZoom.set(clampedZoom);
      this.cdr.detectChanges();
    }
  }

  /**
   * Game lifecycle event handlers
   * Currently unused but available for future implementation
   */
  protected onGameReady(event: any): void {
    // Hook for game ready event
  }

  protected onGameStart(event: any): void {
    // Hook for game start event
  }

  protected onGameComplete(event: any): void {
    // Hook for game complete event
  }

  protected onGameError(event: any): void {
    // Hook for game error event
  }
}

/**
 * Game Status Interface
 * Standardized status communication from game renderers
 */
export interface GameStatus {
  type: 'idle' | 'playing' | 'complete' | 'error';
  progress?: number; // 0-100 for scratchcard scratch progress
  result?: unknown; // GameResult when complete
  error?: string; // Error message if type='error'
}
